import { z } from 'zod/v3'

import type { PickedImage } from '@/components/retail/cms/post-form-model'
import type {
    ShopPageEdit,
    ShopPageInput,
    ShopPageKind,
} from '@/lib/api/shop-pages-api'
import {
    POST_EXCERPT_MAX,
    POST_SEO_DESC_MAX,
    POST_SEO_TITLE_MAX,
    POST_SLUG_MAX,
    POST_TITLE_MAX,
} from '@/components/retail/cms/post-form-model'
import { m } from '@/lib/paraglide/messages'

// Form soạn trang / câu chuyện (khớp server usecase/shop/pages.go).

/** Đường dẫn có sẵn của website — trang thường không được dùng (như server). */
export const RESERVED_PAGE_SLUGS = [
    'admin',
    'api',
    'erp-hooks',
    'erp-media',
    'mock',
    'danh-muc',
    'dat-hang',
    'dat-lai-mat-khau',
    'san-pham',
    'tai-khoan',
    'thanh-toan',
    'tim-kiem',
    'tin-tuc',
    'tra-cuu-don',
    'xac-minh-email',
    'xem-truoc',
]

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

export interface PageForm {
    title: string
    slug: string
    summary: string
    cover: PickedImage | null
    og: PickedImage | null
    content_html: string
    seo_title: string
    seo_description: string
}

const pick = (
    id: string | null,
    media: ShopPageEdit['media'],
): PickedImage | null => {
    if (!id) return null
    const img = media[id]
    return img
        ? { id, url: img.url, thumb_url: img.thumb_url, alt: img.alt }
        : { id, url: '' }
}

export const emptyPageForm = (): PageForm => ({
    title: '',
    slug: '',
    summary: '',
    cover: null,
    og: null,
    content_html: '',
    seo_title: '',
    seo_description: '',
})

export function pageToForm(p: ShopPageEdit): PageForm {
    const f = p.fields
    return {
        title: f.title,
        slug: f.slug,
        summary: f.summary ?? '',
        cover: pick(f.cover_media_id, p.media),
        og: pick(f.og_media_id, p.media),
        content_html: f.content_html ?? '',
        seo_title: f.seo_title ?? '',
        seo_description: f.seo_description ?? '',
    }
}

const opt = (s: string) => (s.trim() === '' ? null : s.trim())

export function pageFormToInput(
    kind: ShopPageKind,
    f: PageForm,
    version?: number,
): ShopPageInput {
    const story = kind === 'story'
    return {
        ...(version === undefined ? { kind } : { version }),
        title: f.title.trim(),
        slug: f.slug.trim(),
        summary: story ? opt(f.summary) : null,
        cover_media_id: story ? (f.cover?.id ?? null) : null,
        content_html: opt(f.content_html),
        seo_title: opt(f.seo_title),
        seo_description: opt(f.seo_description),
        og_media_id: f.og?.id ?? null,
    }
}

const max = (n: number) => m.error_shop_field_too_long({ max: String(n) })

export const createPageSchema = (kind: ShopPageKind) =>
    z
        .object({
            title: z
                .string()
                .trim()
                .min(1, m.cmsPage_error_title_required())
                .max(POST_TITLE_MAX, max(POST_TITLE_MAX)),
            slug: z
                .string()
                .trim()
                .max(POST_SLUG_MAX, max(POST_SLUG_MAX))
                .refine(
                    (s) => s === '' || SLUG_RE.test(s),
                    m.cmsPost_error_slug(),
                )
                .refine(
                    (s) => kind !== 'page' || !RESERVED_PAGE_SLUGS.includes(s),
                    m.cmsPage_error_slug_reserved(),
                ),
            summary: z
                .string()
                .trim()
                .max(POST_EXCERPT_MAX, max(POST_EXCERPT_MAX)),
            seo_title: z
                .string()
                .trim()
                .max(POST_SEO_TITLE_MAX, max(POST_SEO_TITLE_MAX)),
            seo_description: z
                .string()
                .trim()
                .max(POST_SEO_DESC_MAX, max(POST_SEO_DESC_MAX)),
        })
        .passthrough()

/** Tên ô form của lỗi server (params.field). */
export function pageFormField(field: string): string {
    if (field === 'cover_media_id') return 'cover'
    if (field === 'og_media_id') return 'og'
    return field
}

export function pageKindLabel(kind: ShopPageKind): string {
    return kind === 'story' ? m.cmsPage_kind_story() : m.cmsPage_kind_page()
}

/** Đường dẫn bài trên web để nhân viên biết trang nằm ở đâu. */
export function pageLiveUrl(kind: ShopPageKind, slug: string): string {
    return kind === 'story'
        ? m.cmsPage_live_story({ slug })
        : m.cmsPage_live_page({ slug })
}
